import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError, AxiosResponse, AxiosResponseHeaders } from "axios";
import { Thunk } from "../../store";
import {
  BackendListaTareasTomadasRecurso,
  getCurrentDomain,
} from "../../../apis/backend/Api";
import mock_Json from "../../../dataJson/tareasTomadasRecurso.json";
import {
  formatearMensajeBackend,
  procesarDosPropiedadMapeos,
} from "../../../apis/helper/helperApi";
import { formatearFechaCustom } from "../../../components/common/helper/formatoFecha";
import { TypeStatus } from "../../helpers/constantes";
import { useCasosFormateados } from "../../helpers/useCasosFormateados";

interface State {
  tareasTomadasRecurso: any[];
  status: TypeStatus;
  isLoading: boolean;
  Success: boolean;
  Mensaje: string | null;
  error: string | null;
}

const initialState: State = {
  tareasTomadasRecurso: [],
  status: "idle",
  isLoading: false,
  Success: false,
  Mensaje: "",
  error: null,
};
interface defaultResponse {
  data: any;
  status: number;
  statusText: string;
  headers: AxiosResponseHeaders | {};
  config: any;
  request?: any;
}
const defaultResp: defaultResponse = {
  data: undefined,
  status: 0,
  statusText: "",
  headers: {},
  config: undefined,
};
const listaTareasTomadasRecursoSlice = createSlice({
  name: "listaTareasTomadasRecurso",
  initialState,
  reducers: {
    setListaTareasTomadasRecurso: (
      state,
      action: PayloadAction<{ datos: any[]; numPagina: number }>,
    ) => {
      const { datos, numPagina } = action.payload;
      // Si la página es 1, reemplazamos los datos, si no, concatenamos con los existentes
      if (numPagina === 0) {
        state.tareasTomadasRecurso = datos;
      } else {
        state.tareasTomadasRecurso = [...state.tareasTomadasRecurso, ...datos];
      }
    },
    getListaTareasTomadasRecurso: (state, action: PayloadAction<any[]>) => {
      state.tareasTomadasRecurso = action.payload;
    },
    setClearListaTareasTomadasRecurso: (
      state,
      action: PayloadAction<any[]>,
    ) => {
      state = initialState;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setStatus: (state, action: PayloadAction<TypeStatus>) => {
      state.status = action.payload;
    },
    setResult: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setSuccess: (state, action: PayloadAction<boolean>) => {
      state.Success = action.payload;
    },
    setMensaje: (state, action: PayloadAction<string>) => {
      state.Mensaje = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
  },
});
export const setBackendListaTareasTomadasRecurso =
  (Estado: string, Cantidad: number, NumPagina: number): Thunk =>
  async (dispatch): Promise<AxiosResponse | AxiosError> => {
    dispatch(setIsLoading(true));

    try {
      let response: AxiosResponse = await BackendListaTareasTomadasRecurso(
        "0",
        Estado,
        Cantidad,
        NumPagina,
      );

      const isError =
        response.data?.Success === false ||
        (response.data?.ResultadoSolicitud !== undefined &&
          response.data?.ResultadoSolicitud !== 200);

      if (isError) {
        dispatch(setIsLoading(false));
        dispatch(setStatus("failed"));
        dispatch(setSuccess(false));
      } else {
        response.data.Data = procesarDosPropiedadMapeos(
          response.data.Data || [],
          "PrioridadBase",
          "Prioridad",
        );

        let listaFormateada = await useCasosFormateados(response.data.Data, 0);
        let updatelistaFormateada = listaFormateada.map((e: any) => ({
          ...e,
          FechaHoraInicio: formatearFechaCustom(e.FechaHoraInicio),
          UltAct: formatearFechaCustom(e.UltAct),
          FechaVenc: formatearFechaCustom(e.FechaVenc),
          FechaHoraFin: formatearFechaCustom(e.FechaHoraFin),
        }));

        dispatch(
          setListaTareasTomadasRecurso({
            datos: updatelistaFormateada,
            numPagina: NumPagina,
          }),
        );
        dispatch(setIsLoading(false));
        dispatch(setStatus("succeeded"));
        dispatch(setSuccess(true));
      }
      dispatch(setMensaje(formatearMensajeBackend(response.data.Mensaje)));
      return response.data;
    } catch (error: any) {
      dispatch(setIsLoading(false));
      dispatch(setSuccess(false));
      dispatch(setStatus("failed"));

      if (error.response?.status === 401) {
        dispatch(setStatus("noAutorice"));
        dispatch(
          setMensaje(
            formatearMensajeBackend(
              "Tu sesión ha expirado. Vuelve a iniciar sesión",
            ),
          ),
        );
      } else if (error.response) {
        // Hay respuesta del servidor, pero es otro error (400, 422, 500, etc.)
        console.error(
          "Error de servidor:",
          error.response.status,
          error.response.data,
        );

        const errorData = error.response.data;
        const mensajeBackend = errorData?.Mensaje;
        const resultadoSolicitud = errorData?.ResultadoSolicitud;

        if (
          mensajeBackend &&
          (resultadoSolicitud !== 200 || errorData?.Success === false)
        ) {
          dispatch(setMensaje(formatearMensajeBackend(mensajeBackend)));
        } else {
          dispatch(
            setMensaje(
              `Error ${error.response.status}: Ocurrió un problema en el servidor.`,
            ),
          );
        }
      } else if (error.request) {
        console.error("Error de conexión:", error.message);
        dispatch(
          setMensaje(
            "No se pudo conectar al servidor. Por favor, revisa tu conexión a internet o tu VPN.",
          ),
        );
      } else {
        console.error("Error de red o solicitud:", error.message);
        dispatch(
          setMensaje(
            "Ocurrió un problema con la red. Por favor, verifica tu conexión.",
          ),
        );
      }
      return error as AxiosError;
    } finally {
      dispatch(setIsLoading(false));
      if (getCurrentDomain() === "localhost") {
        dispatch(setIsLoading(true));
        let response: AxiosResponse = defaultResp;
        response.data = mock_Json;

        const mockData: any = mock_Json;
        const isErrorMock =
          mockData?.Success === false ||
          (mockData?.ResultadoSolicitud !== undefined &&
            mockData?.ResultadoSolicitud !== 200);

        if (isErrorMock) {
          dispatch(setIsLoading(false));
          dispatch(setStatus("failed"));
          dispatch(setSuccess(false));
        } else {
          response.data.Data = procesarDosPropiedadMapeos(
            mock_Json.Data || [],
            "PrioridadBase",
            "Prioridad",
          );

          let listaFormateada = await useCasosFormateados(response.data.Data, 0);
          let updatelistaFormateada = listaFormateada.map((e: any) => ({
            ...e,
            FechaHoraInicio: formatearFechaCustom(e.FechaHoraInicio),
            UltAct: formatearFechaCustom(e.UltAct),
            FechaVenc: formatearFechaCustom(e.FechaVenc),
            FechaHoraFin: formatearFechaCustom(e.FechaHoraFin),
          }));

          dispatch(
            setListaTareasTomadasRecurso({
              datos: updatelistaFormateada,
              numPagina: NumPagina,
            }),
          );
          dispatch(setIsLoading(false));
          dispatch(setStatus("succeeded"));
          dispatch(setSuccess(true));
        }
        dispatch(setMensaje(formatearMensajeBackend(response.data.Mensaje)));
        const mock401 = await import("../../../dataJson/401.json");
        if (mock401.status === 401) {
          dispatch(setStatus("noAutorice"));
          dispatch(
            setMensaje(
              formatearMensajeBackend(
                "Tu sesión ha expirado. Vuelve a iniciar sesión",
              ),
            ),
          );
        }
      }
    }
  };

export const setBackendClearListaTareasTomadasRecurso =
  (): Thunk =>
  async (dispatch): Promise<AxiosResponse | AxiosError> => {
    dispatch(setIsLoading(true));
    try {
      let response: AxiosResponse = defaultResp;

      dispatch(
        setListaTareasTomadasRecurso({
          datos: [],
          numPagina: 0,
        }),
      );
      dispatch(setIsLoading(false));
      dispatch(setStatus("idle"));
      dispatch(setSuccess(false));
      dispatch(setMensaje(""));
      return response.data;
    } catch (error: any) {
      dispatch(setIsLoading(false));
      dispatch(setStatus("failed"));
      return error as AxiosError;
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const {
  setListaTareasTomadasRecurso,
  getListaTareasTomadasRecurso,
  setClearListaTareasTomadasRecurso,
  setIsLoading,
  setStatus,
  setResult,
  setSuccess,
  setMensaje,
  setError,
} = listaTareasTomadasRecursoSlice.actions;

export default listaTareasTomadasRecursoSlice.reducer;
